import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, SimpleGrid } from '@chakra-ui/react';
import { api } from '@/lib/api';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge, RegimeBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { OutcomesSection } from '@/components/OutcomesPanel';
import type { MarketScanResult, PortfolioGoal } from '@market-buddy/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AutoTradeConfig { enabled: boolean }
interface BrokerAccount { cash: number; total: number; ppl: number; result: number }

interface TechnicalItem {
  id: string;
  symbol: string;
  name: string;
  price: number;
  changePercent: number;
  regime: string | null;
  sentiment: string;
  opportunityScore: number | null;
  position: { quantity: number; pnl: number; pnlPercent: number } | null;
}

interface EdgarItem {
  id: string;
  symbol: string;
  name: string;
  insiderSignal: number;
  daysOnWatchlist: number;
  addedAt: string;
  position: {
    quantity: number;
    averagePrice: number;
    currentPrice: number;
    pnl: number;
    pnlPercent: number;
  } | null;
}

interface SplitWatchlist {
  technical: TechnicalItem[];
  edgar: EdgarItem[];
  meta: { technicalCount: number; edgarCount: number };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatGbp(value: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
}

function PnlText({ pnl, pnlPercent }: { pnl: number; pnlPercent: number }) {
  const colour = pnl >= 0 ? 'status.success.text' : 'status.error.text';
  return (
    <Text fontSize="12px" fontFamily="mono" color={colour}>
      {pnl >= 0 ? '+' : ''}{formatGbp(pnl)} ({pnlPercent >= 0 ? '+' : ''}{pnlPercent.toFixed(1)}%)
    </Text>
  );
}

function SignalBar({ signal }: { signal: number }) {
  const pct = Math.min(100, signal * 100);
  const colour =
    signal >= 0.85 ? 'status.success.text' :
    signal >= 0.5 ? 'status.warning.text' :
    'text.tertiary';
  return (
    <Flex align="center" gap="2" flex="1" maxW="100px">
      <Box w="100%" h="4px" bg="bg.tertiary" borderRadius="full" overflow="hidden">
        <Box h="100%" w={`${pct}%`} bg={colour} borderRadius="full" />
      </Box>
      <Text fontSize="11px" color="text.tertiary" flexShrink={0}>{signal.toFixed(2)}</Text>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function HealthGauge({ score }: { score: number }) {
  const colour =
    score >= 70 ? 'status.success.text' :
    score >= 40 ? 'status.warning.text' :
    'status.error.text';
  return (
    <Flex align="center" gap="3">
      <Text fontSize="48px" fontWeight="700" lineHeight="1" color={colour}>{score}</Text>
      <Text fontSize="14px" color="text.tertiary" lineHeight="1.2">/ 100</Text>
    </Flex>
  );
}

function GoalTracker({ goal, currentValue }: { goal: PortfolioGoal; currentValue: number }) {
  // Honest framing: measure everything from the STARTING value, not from zero.
  const changeFromStart = currentValue - goal.startingValue;
  const changePct = goal.startingValue > 0 ? (changeFromStart / goal.startingValue) * 100 : 0;
  const belowStart = changeFromStart < 0;

  // Progress = share of the start→target journey actually covered.
  const journey = goal.targetValue - goal.startingValue;
  const rawProgress = journey > 0 ? (changeFromStart / journey) * 100 : 0;
  const progress = Math.min(100, Math.max(0, rawProgress));

  const daysRemaining = Math.max(0, Math.floor((new Date(goal.targetDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  const monthsRemaining = Math.floor(daysRemaining / 30);
  const timeLeft = monthsRemaining > 0
    ? `${monthsRemaining} month${monthsRemaining !== 1 ? 's' : ''} remaining`
    : `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`;

  // Required pace to still hit the target by the deadline.
  const needed = Math.max(0, goal.targetValue - currentValue);
  const monthsLeftExact = Math.max(daysRemaining / 30.44, 0);
  const perMonth = monthsLeftExact >= 1 ? needed / monthsLeftExact : needed;
  const targetLabel = new Date(goal.targetDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  const paceLine = needed <= 0
    ? 'Target reached'
    : daysRemaining <= 0
      ? `Target date passed — still ${formatGbp(needed)} short`
      : `Needs +${formatGbp(perMonth)}/month to hit ${formatGbp(goal.targetValue)} by ${targetLabel}`;

  const changeColour = belowStart ? 'status.error.text' : 'status.success.text';
  const barColour = belowStart ? 'status.error.text'
    : progress >= 75 ? 'status.success.text'
    : progress >= 40 ? 'status.warning.text'
    : 'text.brand';

  return (
    <Card>
      <CardHeader>
        <Text fontSize="14px" fontWeight="500" color="text.secondary">Portfolio Goal</Text>
        <Badge variant="brand">{timeLeft}</Badge>
      </CardHeader>
      <Flex justify="space-between" align="baseline" mb="2">
        <Text fontSize="24px" fontWeight="700" color="text.primary">{formatGbp(currentValue)}</Text>
        <Text fontSize="14px" fontFamily="mono" color={changeColour}>
          {belowStart ? '-' : '+'}{formatGbp(Math.abs(changeFromStart))} ({changePct >= 0 ? '+' : ''}{changePct.toFixed(1)}%) vs start
        </Text>
      </Flex>
      <Box w="100%" h="8px" bg="bg.tertiary" borderRadius="full" overflow="hidden" mb="3">
        <Box h="100%" w={`${progress}%`} bg={barColour} borderRadius="full" transition="width 500ms ease" />
      </Box>
      <Flex justify="space-between">
        <Text fontSize="12px" color={belowStart ? 'status.error.text' : 'text.tertiary'}>
          {belowStart
            ? `Below start — ${formatGbp(Math.abs(changeFromStart))} down on ${formatGbp(goal.startingValue)}`
            : `${progress.toFixed(1)}% of the way from ${formatGbp(goal.startingValue)} to ${formatGbp(goal.targetValue)}`}
        </Text>
        <Text fontSize="12px" color="text.tertiary">{formatGbp(needed)} to go</Text>
      </Flex>
      <Flex justify="space-between" mt="2">
        <Text fontSize="12px" color="text.tertiary">{paceLine}</Text>
      </Flex>
    </Card>
  );
}

function TechnicalList({ items, navigate }: { items: TechnicalItem[]; navigate: (p: string) => void }) {
  return (
    <Card>
      <CardHeader>
        <Flex align="center" gap="2">
          <Text fontSize="14px" fontWeight="500" color="text.secondary">Technical Watchlist</Text>
          <Badge variant="brand">{items.length}</Badge>
        </Flex>
        <Text fontSize="11px" color="text.tertiary">5-min candles &middot; 10-min rotation</Text>
      </CardHeader>
      {items.length === 0 ? (
        <Text fontSize="13px" color="text.tertiary">
          No technical instruments yet. The daily scan will populate this.
        </Text>
      ) : (
        <Flex direction="column" gap="1px">
          {items.map((item) => (
            <Flex
              key={item.id}
              as="button"
              onClick={() => navigate(`/cockpit/${item.symbol}`)}
              align="center"
              justify="space-between"
              py="2"
              px="1"
              borderRadius="md"
              cursor="pointer"
              border="none"
              bg="transparent"
              w="100%"
              _hover={{ bg: 'bg.tertiary' }}
            >
              <Flex align="center" gap="3" flex="1" minW="0">
                <Box>
                  <Text fontSize="13px" fontWeight="600" color="text.primary">{item.symbol}</Text>
                  <Text fontSize="11px" color="text.tertiary" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" maxW="120px">
                    {item.name}
                  </Text>
                </Box>
              </Flex>
              <Flex align="center" gap="3">
                {item.regime && <RegimeBadge regime={item.regime} />}
                {item.position && (
                  <PnlText pnl={item.position.pnl} pnlPercent={item.position.pnlPercent} />
                )}
                {!item.position && item.price > 0 && (
                  <Text fontSize="12px" color={item.changePercent >= 0 ? 'status.success.text' : 'status.error.text'} fontFamily="mono">
                    {item.changePercent >= 0 ? '+' : ''}{item.changePercent.toFixed(2)}%
                  </Text>
                )}
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}
    </Card>
  );
}

function EdgarList({ items }: { items: EdgarItem[] }) {
  return (
    <Card>
      <CardHeader>
        <Flex align="center" gap="2">
          <Text fontSize="14px" fontWeight="500" color="text.secondary">Insider Picks</Text>
          <Badge variant="warning">{items.length}</Badge>
        </Flex>
        <Text fontSize="11px" color="text.tertiary">EDGAR Form 4 &middot; daily evaluation</Text>
      </CardHeader>
      {items.length === 0 ? (
        <Text fontSize="13px" color="text.tertiary">
          No insider-driven picks. EDGAR scanner runs daily at 22:00 UTC.
        </Text>
      ) : (
        <Flex direction="column" gap="3">
          {items.map((item) => (
            <Box key={item.id}>
              <Flex align="center" justify="space-between" mb="1">
                <Flex align="center" gap="2">
                  <Text fontSize="13px" fontWeight="600" color="text.primary">{item.symbol}</Text>
                  <Text fontSize="11px" color="text.tertiary">{item.name}</Text>
                </Flex>
                <Text fontSize="11px" color="text.tertiary">{item.daysOnWatchlist}d</Text>
              </Flex>
              <Flex align="center" justify="space-between" gap="3">
                <Flex align="center" gap="2">
                  <Text fontSize="11px" color="text.tertiary">Signal</Text>
                  <SignalBar signal={item.insiderSignal} />
                </Flex>
                {item.position ? (
                  <Flex align="center" gap="2">
                    <Text fontSize="11px" color="text.tertiary">{item.position.quantity} shares</Text>
                    <PnlText pnl={item.position.pnl} pnlPercent={item.position.pnlPercent} />
                  </Flex>
                ) : (
                  <Badge variant="neutral">Awaiting entry</Badge>
                )}
              </Flex>
            </Box>
          ))}
        </Flex>
      )}
    </Card>
  );
}

function ScanSkeleton() {
  return (
    <Flex direction="column" gap="5">
      <Skeleton width="200" height={48} />
      <Skeleton width="100%" height={16} />
      <Skeleton width="80%" height={16} />
      <SimpleGrid columns={{ base: 1, md: 2 }} gap="4" mt="2">
        <Skeleton width="100%" height={120} />
        <Skeleton width="100%" height={120} />
      </SimpleGrid>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function Dashboard() {
  const navigate = useNavigate();
  const [scan, setScan] = useState<MarketScanResult | null>(null);
  const [autoTrade, setAutoTrade] = useState<AutoTradeConfig | null>(null);
  const [goal, setGoal] = useState<PortfolioGoal | null>(null);
  const [account, setAccount] = useState<BrokerAccount | null>(null);
  const [split, setSplit] = useState<SplitWatchlist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [scanResult, atConfig, goalResult, acctResult, splitResult] = await Promise.allSettled([
        api.get<MarketScanResult>('/api/scanner/latest'),
        api.get<AutoTradeConfig>('/api/broker/auto-trade'),
        api.get<PortfolioGoal | null>('/api/goals'),
        api.get<BrokerAccount>('/api/broker/account'),
        api.get<SplitWatchlist>('/api/watchlist/split'),
      ]);

      if (scanResult.status === 'fulfilled') setScan(scanResult.value);
      if (atConfig.status === 'fulfilled') setAutoTrade(atConfig.value);
      if (goalResult.status === 'fulfilled') setGoal(goalResult.value);
      if (acctResult.status === 'fulfilled') setAccount(acctResult.value);
      if (splitResult.status === 'fulfilled') setSplit(splitResult.value);
    } catch {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box>
      {/* Header */}
      <Flex align="center" justify="space-between" mb="6">
        <Box>
          <Text fontSize="24px" fontWeight="600" color="text.primary">Portfolio Dashboard</Text>
          <Text fontSize="14px" color="text.tertiary" mt="1">
            {scan?.scannedAt ? `Last scan: ${timeAgo(scan.scannedAt)}` : 'Waiting for first daily scan'}
          </Text>
        </Box>
        {autoTrade && (
          <Badge variant={autoTrade.enabled ? 'success' : 'neutral'}>
            Auto-trade {autoTrade.enabled ? 'ON' : 'OFF'}
          </Badge>
        )}
      </Flex>

      {loading ? (
        <ScanSkeleton />
      ) : error ? (
        <Card><Text color="text.secondary">{error}</Text></Card>
      ) : (
        <Flex direction="column" gap="5">
          {/* Goal Tracker */}
          {goal ? (
            <GoalTracker goal={goal} currentValue={account?.total ?? 0} />
          ) : (
            <Card>
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="14px" fontWeight="500" color="text.secondary">Portfolio Goal</Text>
                  <Text fontSize="13px" color="text.tertiary" mt="1">Set a target to track your progress</Text>
                </Box>
                <Button variant="secondary" onClick={() => navigate('/settings')}>Set Goal</Button>
              </Flex>
            </Card>
          )}

          {/* Realised outcomes + open-position protection */}
          <OutcomesSection />

          {/* Two strategy lists side by side */}
          <SimpleGrid columns={{ base: 1, md: 2 }} gap="4">
            <TechnicalList items={split?.technical ?? []} navigate={navigate} />
            <EdgarList items={split?.edgar ?? []} />
          </SimpleGrid>

          {/* Health + Scan results */}
          {scan?.portfolioHealth && (
            <>
              <Card>
                <CardHeader>
                  <Text fontSize="14px" fontWeight="500" color="text.secondary">Portfolio Health</Text>
                </CardHeader>
                <HealthGauge score={scan.portfolioHealth.score} />
                <Text fontSize="14px" color="text.primary" mt="3">{scan.portfolioHealth.summary}</Text>
                {scan.portfolioHealth.concerns.length > 0 && (
                  <Flex direction="column" gap="2" mt="3">
                    {scan.portfolioHealth.concerns.map((c, i) => (
                      <Flex key={i} align="flex-start" gap="2">
                        <Text fontSize="13px" color="status.warning.text" flexShrink={0}>!</Text>
                        <Text fontSize="13px" color="text.secondary">{c}</Text>
                      </Flex>
                    ))}
                  </Flex>
                )}
              </Card>

              {/* Suggestions */}
              <SimpleGrid columns={{ base: 1, md: 2 }} gap="4">
                <Card>
                  <CardHeader>
                    <Text fontSize="14px" fontWeight="500" color="text.secondary">Suggested Additions</Text>
                    <Badge variant="brand">{scan.suggestAdd.length}</Badge>
                  </CardHeader>
                  {scan.suggestAdd.length === 0 ? (
                    <Text fontSize="13px" color="text.tertiary">No additions suggested.</Text>
                  ) : (
                    <Flex direction="column" gap="3">
                      {scan.suggestAdd.map((s) => (
                        <Box key={s.symbol}>
                          <Flex align="center" gap="2" mb="1">
                            <Text fontSize="14px" fontWeight="600" color="text.primary">{s.symbol}</Text>
                            <Text fontSize="12px" color="text.tertiary">{s.sector}</Text>
                          </Flex>
                          <Text fontSize="13px" color="text.secondary">{s.reason}</Text>
                        </Box>
                      ))}
                    </Flex>
                  )}
                </Card>
                <Card>
                  <CardHeader>
                    <Text fontSize="14px" fontWeight="500" color="text.secondary">Suggested Removals</Text>
                    <Badge variant={scan.suggestRemove.length > 0 ? 'warning' : 'neutral'}>{scan.suggestRemove.length}</Badge>
                  </CardHeader>
                  {scan.suggestRemove.length === 0 ? (
                    <Text fontSize="13px" color="text.tertiary">No removals suggested.</Text>
                  ) : (
                    <Flex direction="column" gap="3">
                      {scan.suggestRemove.map((s) => (
                        <Box key={s.symbol}>
                          <Text fontSize="14px" fontWeight="600" color="text.primary" mb="1">{s.symbol}</Text>
                          <Text fontSize="13px" color="text.secondary">{s.reason}</Text>
                        </Box>
                      ))}
                    </Flex>
                  )}
                </Card>
              </SimpleGrid>

              {/* Market Notes */}
              {scan.marketNotes.length > 0 && (
                <Card>
                  <CardHeader>
                    <Text fontSize="14px" fontWeight="500" color="text.secondary">Market Notes</Text>
                  </CardHeader>
                  <Flex direction="column" gap="2">
                    {scan.marketNotes.map((note, i) => (
                      <Flex key={i} align="flex-start" gap="2">
                        <Text fontSize="13px" color="text.tertiary" flexShrink={0}>&bull;</Text>
                        <Text fontSize="13px" color="text.secondary">{note}</Text>
                      </Flex>
                    ))}
                  </Flex>
                </Card>
              )}

              <Text fontSize="12px" color="text.tertiary" textAlign="center">
                Next scan scheduled: {new Date(scan.nextScanAt).toLocaleString()}
              </Text>
            </>
          )}

          {!scan && (
            <Card>
              <Flex direction="column" align="center" py="8" gap="3">
                <Text fontSize="14px" color="text.tertiary" textAlign="center" maxW="400px">
                  Daily scan runs at 22:00 UTC. Health score, suggestions, and market notes will appear here.
                </Text>
              </Flex>
            </Card>
          )}
        </Flex>
      )}
    </Box>
  );
}
